/**
 * Permission keys. Roles store a subset of these; users may have allow/deny overrides (D-29).
 * Adding a key: add it here + to a group below + the Hebrew label in PERMISSION_LABELS_HE.
 */
export const PERMISSIONS = [
  // dashboard / logs
  'dashboard.view',
  'logs.messages.view',
  'logs.events.view',
  'audit.view',
  // chats (D-71)
  'chats.view',
  'chats.send',
  'chats.send_private_new', // send to a number that never wrote to us (warning flow)
  'chats.delete',
  // NIVISION clients module
  'clients.view',
  'clients.edit',
  'clients.activate', // draft -> active (starts real sends)
  'clients.secrets.write', // sak_ keys + webhook secrets (write-only)
  'clients.portal', // client staff accesses to the alerts center (D-129): create / reset / revoke / see links
  'alerts.edit',
  'alerts.cases.view', // D-126: the staff alerts center (inbox + monthly critical view)
  'alerts.cases.handle', // D-126: status, notes and bulk status in the alerts center
  'templates.edit',
  'summaries.edit',
  'summaries.send_test',
  // WhatsApp
  'channels.view',
  'channels.connect', // QR / pairing / restart
  'channels.edit_limits', // pacing params — SUPER_ADMIN by default
  'channels.profile.edit', // manual profile edit only (D-27)
  'groups.view',
  'groups.manage', // join by link, edit, participants, leave
  'contacts.view',
  'contacts.manage', // block / unblock / edit
  // safety
  'killswitch.toggle',
  'outbox.resend',
  'broadcast.send', // Task 23: one message to every group of the selected clients (step-up)
  // Public API
  'api_keys.view',
  'api_keys.manage',
  // settings
  'settings.view',
  'settings.edit',
  'users.view',
  'users.manage',
  'roles.manage',
  'incidents.ack',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const SYSTEM_ROLES = ['SUPER_ADMIN', 'ADMIN', 'USER', 'VIEWER'] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

const VIEW_ONLY: Permission[] = PERMISSIONS.filter(
  (p) => p.endsWith('.view') && p !== 'api_keys.view' && p !== 'audit.view',
);

export const DEFAULT_ROLE_PERMISSIONS: Record<SystemRole, Permission[]> = {
  SUPER_ADMIN: [...PERMISSIONS],
  ADMIN: PERMISSIONS.filter((p) => p !== 'channels.edit_limits' && p !== 'roles.manage'),
  USER: [
    ...VIEW_ONLY,
    'chats.send',
    'clients.edit',
    'alerts.edit',
    'alerts.cases.handle',
    'templates.edit',
    'summaries.edit',
    'summaries.send_test',
    'outbox.resend',
    'incidents.ack',
  ],
  VIEWER: VIEW_ONLY,
};

export const PERMISSION_GROUPS: { titleHe: string; keys: Permission[] }[] = [
  { titleHe: 'כללי ולוגים', keys: ['dashboard.view', 'logs.messages.view', 'logs.events.view', 'audit.view'] },
  { titleHe: "צ'אטים", keys: ['chats.view', 'chats.send', 'chats.send_private_new', 'chats.delete'] },
  {
    titleHe: 'לקוחות NIVISION',
    keys: [
      'clients.view',
      'clients.edit',
      'clients.activate',
      'clients.secrets.write',
      'clients.portal',
      'alerts.edit',
      'alerts.cases.view',
      'alerts.cases.handle',
      'templates.edit',
      'summaries.edit',
      'summaries.send_test',
    ],
  },
  {
    titleHe: 'וואטסאפ',
    keys: [
      'channels.view',
      'channels.connect',
      'channels.edit_limits',
      'channels.profile.edit',
      'groups.view',
      'groups.manage',
      'contacts.view',
      'contacts.manage',
    ],
  },
  { titleHe: 'בטיחות', keys: ['killswitch.toggle', 'outbox.resend', 'broadcast.send', 'incidents.ack'] },
  { titleHe: 'API', keys: ['api_keys.view', 'api_keys.manage'] },
  { titleHe: 'הגדרות', keys: ['settings.view', 'settings.edit', 'users.view', 'users.manage', 'roles.manage'] },
];

export const PERMISSION_LABELS_HE: Record<Permission, string> = {
  'dashboard.view': 'צפייה בדשבורד',
  'logs.messages.view': 'לוג הודעות',
  'logs.events.view': 'לוג אירועי NIVISION',
  'audit.view': 'יומן פעולות (audit)',
  'chats.view': "צפייה בצ'אטים",
  'chats.send': "שליחה בצ'אטים",
  'chats.send_private_new': 'שליחה למספר חדש בפרטי',
  'chats.delete': 'מחיקת הודעות',
  'clients.view': 'צפייה בלקוחות',
  'clients.edit': 'עריכת לקוחות',
  'clients.activate': 'הפעלת לקוח (שליחה אמיתית)',
  'clients.secrets.write': 'הזנת מפתחות וסודות',
  'clients.portal': 'ניהול גישת לקוחות למרכז ההתראות',
  'alerts.edit': 'הגדרות התראות',
  'alerts.cases.view': 'צפייה במרכז ההתראות',
  'alerts.cases.handle': 'טיפול בהתראות (סטטוס והערות)',
  'templates.edit': 'עריכת תבניות',
  'summaries.edit': 'הגדרות סיכומים',
  'summaries.send_test': 'שליחת סיכום לבדיקה',
  'channels.view': 'צפייה במספרים',
  'channels.connect': 'חיבור / הפעלה מחדש של מספר',
  'channels.edit_limits': 'עריכת קצב ומכסות',
  'channels.profile.edit': 'עריכת פרופיל המספר',
  'groups.view': 'צפייה בקבוצות',
  'groups.manage': 'ניהול קבוצות',
  'contacts.view': 'צפייה באנשי קשר',
  'contacts.manage': 'ניהול וחסימת אנשי קשר',
  'killswitch.toggle': 'עצירת חירום',
  'outbox.resend': 'שליחה חוזרת',
  'broadcast.send': 'הודעת תפוצה',
  'api_keys.view': 'צפייה במפתחות API',
  'api_keys.manage': 'ניהול מפתחות API',
  'settings.view': 'צפייה בהגדרות',
  'settings.edit': 'עריכת הגדרות',
  'users.view': 'צפייה במשתמשים',
  'users.manage': 'ניהול משתמשים',
  'roles.manage': 'ניהול תפקידים והרשאות',
  'incidents.ack': 'אישור תקלות',
};

/** Effective permissions = role permissions + allow overrides - deny overrides. */
export function effectivePermissions(
  rolePermissions: readonly string[],
  overrides: readonly { permission: string; effect: 'allow' | 'deny' }[],
): Set<Permission> {
  const set = new Set(rolePermissions.filter((p): p is Permission => (PERMISSIONS as readonly string[]).includes(p)));
  for (const o of overrides) {
    if (!(PERMISSIONS as readonly string[]).includes(o.permission)) continue;
    if (o.effect === 'allow') set.add(o.permission as Permission);
    else set.delete(o.permission as Permission);
  }
  return set;
}
